import axios from "axios";
import { FilesAPI } from "./files.api";
import {
  CompleteMultipartUploadData,
  CreateFileData,
  FileStatus,
  StartMultipartUploadData,
  StartMultipartUploadResult,
} from "./files.types";

const AxiosInstance = axios.create({ baseURL: "", headers: {} });

export type UploadProgressType = "upload" | "hash" | "complete" | "error";

export type UploadProgress = {
  type: UploadProgressType;
  percentage: number; // 0-100
  total: number; // total bytes
  loaded: number; // loaded bytes
  uuid: string;
};

export type UploaderApi = {
  startUpload: (
    d: StartMultipartUploadData
  ) => Promise<StartMultipartUploadResult>;
  completeUpload: (d: CompleteMultipartUploadData) => Promise<unknown>;
  markFailed?: (d: { uuid: string }) => Promise<unknown>;
};

export const defaultUploaderApi: UploaderApi = {
  startUpload: FilesAPI.startMultipartUpload,
  completeUpload: FilesAPI.completeMultipartUpload,
  markFailed: ({ uuid }) =>
    FilesAPI.update({ uuid, status: FileStatus.FAILED }),
};

type UploaderOptions = {
  file: File;
  metadata: CreateFileData;
  chunkSize?: number;
  hashFile?: boolean;
  /**
   * Overrides the routes used for the multipart upload. `startUpload` and
   * `completeUpload` fall back to the `/files/*` defaults when omitted. With a
   * custom `startUpload`, `markFailed` is only called if supplied here (routes
   * without a PATCH endpoint omit it), even if `completeUpload` is the
   * default; otherwise it defaults to `/files/*`.
   */
  api?: Partial<UploaderApi>;
  onProgress?: (progress: UploadProgress) => void;
  onComplete?: (result: unknown) => void;
  onError?: (error: Error) => void;
};

export class Uploader {
  private chunkSize: number;
  private file: File;
  private metadata: CreateFileData;
  private uuid: string;
  private hashFile?: boolean;
  private uploadLoaded: number;
  private progressCache: { [key: number]: number } = {};
  private onProgress?: (progress: UploadProgress) => void;
  private onHashProgress?: (progress: any) => void;
  private onComplete?: (result: unknown) => void;
  private onError?: (error: Error) => void;
  private hashWorker: Worker | null;
  private totalParts: number;
  private parts: { PartNumber: number; ETag: string }[];
  private uploadQueue: { PartNumber: number; ETag: string }[];
  private activeThreads: number;
  private uploadUrls: string[];
  private uploadId: string;
  private md5: string | null;
  private sha1: string | null;
  private sha256: string | null;
  private hashingComplete: boolean;
  private api: UploaderApi;
  private finished: boolean;
  private aborted: boolean;
  private abortController: AbortController;

  constructor(options: UploaderOptions) {
    this.chunkSize = options.chunkSize || 1024 * 1024 * 110; // 100MB, allows for 1TB file uploads
    this.file = options.file;
    this.metadata = options.metadata;
    this.uuid = options.metadata.uuid;
    this.hashFile = options.hashFile === undefined ? false : options.hashFile;
    this.md5 = null;
    this.sha1 = null;
    this.sha256 = null;
    this.hashWorker = null;
    this.hashingComplete = false;
    this.finished = false;
    this.aborted = false;
    this.abortController = new AbortController();
    this.api = options.api
      ? {
          startUpload:
            options.api.startUpload ?? defaultUploaderApi.startUpload,
          completeUpload:
            options.api.completeUpload ?? defaultUploaderApi.completeUpload,
          markFailed: options.api.startUpload
            ? options.api.markFailed
            : options.api.markFailed ?? defaultUploaderApi.markFailed,
        }
      : defaultUploaderApi;
    this.activeThreads = 0;
    this.totalParts = Math.ceil(this.file.size / this.chunkSize) || 1; // for empty files, ensure at least 1 part
    this.parts = Array.from({ length: this.totalParts }, (v, i) => ({
      PartNumber: i + 1,
      ETag: "",
    }));
    this.uploadQueue = Object.assign([], this.parts);
    this.uploadUrls = [];
    this.uploadId = "";
    this.uploadLoaded = 0;
    this.onProgress = options.onProgress;
    this.onComplete = options.onComplete;
    this.onError = options.onError;

    this.start();
  }

  private async start() {
    // make API call to start multipart upload and get signed URLs for each part
    let result: StartMultipartUploadResult;
    try {
      result = await this.api.startUpload({
        uuid: this.uuid,
        parts: this.parts,
        file: this.metadata,
      });
    } catch (error) {
      this.fail(toError(error));
      return;
    }

    // the api client resolves `{ success: false, error }` instead of rejecting
    const data = result?.data;
    if (result?.success === false || !data?.signedUrls || !data.uploadId) {
      this.fail(
        new Error("Failed to start upload", {
          cause: (result as { error?: unknown } | undefined)?.error,
        })
      );
      return;
    }

    // aborted while the start request was in flight
    if (this.finished) return;

    this.uploadUrls = data.signedUrls;
    this.uploadId = data.uploadId;

    // begin hashing file
    if (this.hashFile) {
      this.startHashWorker();
    } else {
      this.hashingComplete = true;
    }

    const maxThreads = this.parts.length > 6 ? 6 : this.parts.length;

    // start 6 uploads at a time
    for (const i of Array.from({ length: maxThreads }, (v, i) => i)) {
      const part = this.uploadQueue.shift();
      if (part) {
        this.uploadPart(part);
      }
    }
  }

  private startHashWorker() {
    this.hashWorker = new Worker(new URL("./hash-worker.js", import.meta.url), {
      type: "module",
    });

    this.hashWorker?.postMessage({
      file: this.file,
      // chunkSize: Math.ceil(this.file.size / 100), // chunk size for hashing, split into 100 parts
      chunkSize:
        this.file.size < 1024 * 1024 * 5 ? this.file.size : 1024 * 1024 * 5, // chunk size for hashing, split into 5MB parts
    });

    this.hashWorker &&
      (this.hashWorker.onmessage = (e) => {
        const { status, md5, sha1, sha256 } = e.data;

        if (status === "complete") {
          this.md5 = md5;
          this.sha1 = sha1;
          this.sha256 = sha256;
          this.hashingComplete = true;
          this.hashWorker?.terminate();
          this.hashWorker = null; // Terminate the worker after use
          this.onProgress?.({
            type: "hash",
            percentage: e.data.progress,
            total: e.data.total,
            loaded: e.data.loaded,
            uuid: this.uuid,
          });

          this.finish();
        } else if (status === "running") {
          this.onHashProgress?.(e.data);
          this.onProgress?.({
            type: "hash",
            percentage: e.data.progress,
            total: e.data.total,
            loaded: e.data.loaded,
            uuid: this.uuid,
          });
        }
        if (status === "error") {
          this.fail(new Error("Hashing error"), true);
        }
      });
  }

  private handleUploadProgress(part: number, event: ProgressEvent) {
    if (this.file) {
      if (
        event.type === "progress" ||
        event.type === "error" ||
        event.type === "abort"
      ) {
        this.progressCache[part] = event.loaded;
      }

      if (event.type === "uploaded") {
        this.uploadLoaded += this.progressCache[part] || 0;
        delete this.progressCache[part];
      }

      const inProgress = Object.keys(this.progressCache)
        .map(Number)
        .reduce((memo, id) => (memo += this.progressCache[id]), 0);

      const sent = Math.min(this.uploadLoaded + inProgress, this.file.size);

      const total = this.file.size;

      const percentage = Math.round((sent / total) * 100);

      this.onProgress?.({
        type: "upload",
        percentage: percentage,
        total: total,
        loaded: sent,
        uuid: this.uuid,
      });
    }
  }

  private async uploadPart(part: { PartNumber: number }) {
    return new Promise(async (resolve, reject) => {
      // upload part
      const sentSize = (part.PartNumber - 1) * this.chunkSize;
      const chunk = this.file.slice(sentSize, sentSize + this.chunkSize);

      this.activeThreads++;
      // upload chunk
      AxiosInstance.request({
        url: this.uploadUrls[part.PartNumber - 1],
        method: "PUT",
        data: chunk,
        maxContentLength: Infinity,
        signal: this.abortController.signal,
        onUploadProgress: (progress: ProgressEvent) =>
          this.handleUploadProgress(part.PartNumber, progress),
        headers: {},
      })
        .then((result) => {
          const ETag = result.headers["etag"] || result.headers["Etag"];
          this.activeThreads--;
          this.parts[part.PartNumber - 1].ETag = ETag || "";

          this.sendNext();

          resolve(result.status);
        })
        .catch(async (error) => {
          await this.fail(toError(error), true);
          resolve(500);
        });
    });
  }

  private sendNext() {
    if (this.finished) return;
    const part = this.uploadQueue.shift();
    if (part) {
      this.uploadPart(part);
    } else {
      this.finish();
    }
  }

  /**
   * Stops the upload without reporting an error or completion. Local work
   * only: the caller owns any server side cleanup of the file record.
   */
  abort() {
    this.aborted = true;
    this.finished = true;
    this.uploadQueue = [];
    this.terminateHashWorker();
    this.abortController.abort();
  }

  private terminateHashWorker() {
    this.hashWorker?.terminate();
    this.hashWorker = null;
  }

  // reports a single failure and stops any further work
  private async fail(error: Error, markFailed = false) {
    if (this.finished) return;
    this.finished = true;
    this.uploadQueue = [];
    this.terminateHashWorker();
    // stop the other in flight parts so they do not compete with the next file
    this.abortController.abort();
    await this.reportFailure(error, markFailed);
  }

  private async reportFailure(error: Error, markFailed: boolean) {
    if (markFailed && this.api.markFailed) {
      try {
        await this.api.markFailed({ uuid: this.uuid });
      } catch {
        // the original error is what matters to the caller
      }
    }
    if (this.aborted) return;
    this.onError?.(error);
  }

  private async finish(data?: { status?: FileStatus }) {
    if (this.finished) return;
    if (this.hashingComplete && this.activeThreads === 0) {
      this.finished = true;
      let result: unknown;
      try {
        result = await this.api.completeUpload({
          uuid: this.uuid,
          uploadId: this.uploadId,
          parts: this.parts,
          md5: this.md5 || undefined,
          sha1: this.sha1 || undefined,
          sha256: this.sha256 || undefined,
          status: data?.status,
        });
      } catch (error) {
        if (this.aborted) return;
        // Never mark FAILED here: the server sets the row ACTIVE as soon as the
        // object is assembled, so a failure (or lost response) after that point
        // would flip a fully stored file to failed.
        await this.reportFailure(toError(error), false);
        return;
      }

      if (this.aborted) return;
      const failed = result as
        | { success?: boolean; error?: unknown }
        | undefined;
      if (failed?.success === false) {
        await this.reportFailure(
          new Error("Failed to complete upload", { cause: failed.error }),
          false
        );
        return;
      }
      this.onComplete?.(result);
    }
  }
}

const toError = (error: unknown) =>
  error instanceof Error ? error : new Error(String(error));
